/**
 * Stage B/F — what a bay tile on a physical floor view shows.
 *
 * The tile's OCCUPANT is only a vehicle that current physical truth puts in
 * the bay (the tickets passed in have already been placed by
 * `placeByPhysicalTruth`). The bay technician's next scheduled job is a
 * different fact — a plan — and is returned separately as `upNext`, never as
 * the occupant. Before Stage B/F ShopFloor drew that next job inside the bay
 * as if the car were there, and even preferred it over a car that was.
 */
export interface FloorTicket {
  id: string;
  bay_id?: string | null;
  status?: string | null;
  timer_status?: string | null;
  assigned_tech_id?: string | null;
  start_time?: string | null;
}

export interface BayTile<T extends FloorTicket> {
  occupant: T | null;
  upNext: T | null;
}

export function bayTile<T extends FloorTicket>(
  bayId: string,
  assignedTechId: string | null,
  tickets: readonly T[],
): BayTile<T> {
  const inBay = tickets.filter((t) => t.bay_id === bayId);
  const occupant =
    inBay.find((t) => t.timer_status === 'running' && t.status === 'Active')
    ?? inBay.find((t) => t.status === 'Paused')
    ?? inBay.find((t) => t.status === 'Active' || t.status === 'Paused')
    ?? null;

  let upNext: T | null = null;
  if (assignedTechId) {
    upNext = tickets
      .filter((t) => t.assigned_tech_id === assignedTechId && t.status === 'Active'
        && t.id !== occupant?.id && t.bay_id !== bayId)
      .sort((a, b) => new Date(a.start_time ?? 0).getTime() - new Date(b.start_time ?? 0).getTime())[0] ?? null;
  }
  return { occupant, upNext };
}
